/**
 * Build the public EventCard location line.
 * Prefer city+state; fall back to street address; never treat ", " as a real location.
 * Online / on-demand online with no venue → "Remote Online Training" (not "Location TBD").
 */

import { coerceEventListingPlainText } from '../utils/coerceEventListingText';

export const LOCATION_TBD = 'Location TBD';
export const REMOTE_ONLINE_TRAINING = 'Remote Online Training';

/** Labels that are not a real place (Arlo "Live online", TBD, etc.). */
const PLACEHOLDER_LOCATION_RE =
  /^(location\s+tbd|tbd|n\/?a|unknown|live\s+online|see event page)$/i;

/** Venue-name sentinels that must not render as a place of business. */
const VIRTUAL_VENUE_NAME_RE =
  /^(location\s+tbd|tbd|n\/?a|unknown|live\s+online|remote\s+online\s+training|see event page)$/i;

/** GoDaddy OLA / Instructor Z pointer into description body — not a place. */
function isSeeDescriptionSentinel(value: string): boolean {
  return /see\s+description/i.test(value);
}

function isBlankOrCommaOnly(value: string): boolean {
  const cleaned = value.replace(/,/g, '').trim();
  return cleaned.length === 0;
}

export function isOnlineDeliveryMode(mode?: string | null): boolean {
  const m = coerceEventListingPlainText(mode, '')
    .toLowerCase()
    .replace(/_/g, '-')
    .trim();
  return m === 'online' || m === 'on-demand-online';
}

/** True for blank / virtual venue names (e.g. Arlo "Live online"). */
export function isVirtualOrPlaceholderVenueLabel(value?: string | null): boolean {
  const trimmed = coerceEventListingPlainText(value, '');
  if (!trimmed || isBlankOrCommaOnly(trimmed)) return true;
  if (isSeeDescriptionSentinel(trimmed)) return true;
  return VIRTUAL_VENUE_NAME_RE.test(trimmed);
}

function isPlaceholderLocationField(value: string): boolean {
  if (!value || isBlankOrCommaOnly(value)) return true;
  if (isSeeDescriptionSentinel(value)) return true;
  return PLACEHOLDER_LOCATION_RE.test(value.trim());
}

export function resolveEventCardLocationLabel(input: {
  location?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  venueState?: string | null;
  mode?: string | null;
}): string {
  const city = coerceEventListingPlainText(input.venueCity, '');
  const state = coerceEventListingPlainText(input.venueState, '');
  const address = coerceEventListingPlainText(input.venueAddress, '');
  const locRaw = coerceEventListingPlainText(input.location, '');
  const loc = isPlaceholderLocationField(locRaw) ? '' : locRaw;
  const online = isOnlineDeliveryMode(input.mode);

  if (city && state) return `${city}, ${state}`;

  if (address) {
    const region = [city, state].filter(Boolean).join(', ');
    return region ? `${address}, ${region}` : address;
  }

  if (city || state) return city || state;

  if (loc) return loc;

  if (online) return REMOTE_ONLINE_TRAINING;

  return LOCATION_TBD;
}
